// @ts-check
import { createId } from '../utils/id'
import {
  nullIfEmpty,
  removeKeysWithNullishValues,
  replaceObjectRawKeys,
} from '../utils/object'
import { createSelector } from '../utils/selector-utils'
import { selectIVEnabled } from './case-props'
import { selectHasFluidDoseDetail } from './drugs-administered'

/**
 * An array that contains all possible IvSolutionType values.
 *
 * @type {IvSolutionType[]}
 */
export const IvSolutionTypes = ['NS', 'LR', 'D5W', 'D5NS', 'D5LR', 'None']

/** @param {Partial<SimplifiedIvDetails>} obj */
export const actionUpdateFirstIvSite = obj => {
  const changes = {}
  for (const key in obj) {
    const value = obj[key]
    if (key === 'solutionType') {
      changes[`ivLocations.0.solutions.0.name`] = value
    } else if (key === 'location') {
      changes[`ivLocations.0.name`] = value
    } else if (key === 'amount') {
      changes[`ivLocations.0.solutions.0.amount`] = value
    } else {
      changes[`ivLocations.0.${key}`] = value
    }
  }
  return changes
}

/** @param {string} [preGeneratedId] */
export const actionAddIvSite = preGeneratedId => ({
  [`ivLocations.${preGeneratedId || createId('iv')}.editing`]: true,
})

/**
 * @param {string} pmtId
 * @param {Partial<IvSiteUpdateProps>} updates
 */
export const actionUpdateIvSite = (pmtId, updates) => {
  const base = `ivLocations.${pmtId}`
  const res = {}
  for (const key /** @type {keyof IvSiteUpdateProps} */ in updates) {
    const typedKey = /** @type {keyof IvSiteUpdateProps} */ (key)
    const value = updates[typedKey]
    if (typedKey === 'solutionType') {
      res[`${base}.solutions.0.name`] = value
    } else if (typedKey === 'location') {
      res[`${base}.name`] = value
    } else {
      res[`${base}.${typedKey}`] = value
    }
  }
  return res
}

/** @param {string} pmtId */
export const actionRemoveIvSite = pmtId => ({
  [`ivLocations.${pmtId}`]: null,
})

/**
 * @param {string} pmtId
 * @param {IvSolutionType} solutionType
 * @param {number} [timeOverride]
 */
export const actionAddIvSolution = (pmtId, solutionType, timeOverride) => {
  const base = `ivLocations.${pmtId}.solutions.${createId('bag')}`
  return {
    [`${base}.name`]: solutionType,
    [`${base}.time`]: timeOverride || Date.now(),
  }
}

/**
 * @param {string} pmtId
 * @param {string} bagId
 * @param {Partial<IvSolutionCaseProps>} updates
 */
export const actionUpdateIvSolution = (pmtId, bagId, updates) => {
  const base = `ivLocations.${pmtId}.solutions.${bagId}`
  const res = {}
  for (const key in updates) {
    const val = updates[key]
    res[`${base}.${key}`] = val
  }
  return res
}

/**
 * @param {string} pmtId
 * @param {string} bagId
 */
export const actionRemoveIvSolution = (pmtId, bagId) => ({
  [`ivLocations.${pmtId}.solutions.${bagId}`]: null,
})

// sort fn for sort by time property if there is not a time sort to first position
const specialTimeSort = (a, b) => {
  // but always sort id of '0' to the top
  if (a.id === '0') {
    return -1
  }
  if (b.id === '0') {
    return 1
  }
  // if both have time sort by time oldest first
  if (a.time && b.time) {
    return a.time - b.time
  }

  // if one of them does not have a time, sort it to the bottom
  if (!a.time && b.time) {
    return 1
  }
  if (a.time && !b.time) {
    return -1
  }
  return 0
}

const empty = []
/**
 * @param {CaseData} caseData
 * @returns {IvSiteBase[]}
 */
export const selectIvSiteArrayRaw = caseData => {
  const raw = caseData.ivLocations
  const events = caseData.events
  /**
   * Legacy support for case ivStart and ivEnd before ivLocations individual
   * start/stop times were supported. Written (Oct, 2024). Can eventually be removed.
   */
  const deprecatedIvStart = events && events.ivStart
  const deprecatedIvEnd = events && events.ivEnd
  if (!raw && !deprecatedIvStart) {
    return empty
  }
  const toReturn = []
  for (const key in raw) {
    toReturn.push({ id: key, ...raw[key] })
  }
  /**
   * If there is only one site, and it does not have a time, but the case has a
   * time, use the case time
   */
  if (toReturn.length === 1) {
    if (!toReturn[0].time && deprecatedIvStart) {
      toReturn[0].time = deprecatedIvStart
    }
    if (!toReturn[0].timeRemoved && deprecatedIvEnd) {
      toReturn[0].timeRemoved = deprecatedIvEnd
    }
  }

  return toReturn.sort(specialTimeSort)
}

/** @type {(keyof IvSite)[]} */
const siteRequiredProps = [
  'name',
  'side',
  'securedWith',
  'attempts',
  'needleGauge',
  'time',
]

/** @param {Omit<IvSite, 'placementComplete'>} site */
const placementIsComplete = site =>
  !!(
    siteRequiredProps.every(key => site[key]) &&
    // has first solution answer
    site.solutions &&
    site.solutions['0'] &&
    site.solutions['0'].name
  )

const emptyArray = []
/** @type {CaseSelector<IvSite[]>} */
export const selectIvSites = createSelector(
  selectIVEnabled,
  selectIvSiteArrayRaw,
  /**
   * @param {boolean} enabled
   * @param {IvSiteBase[]} sites
   * @returns {IvSite[]}
   */
  (enabled, sites) => {
    if (!enabled) {
      return emptyArray
    }
    if (!sites.length) {
      sites = [{ id: '0' }]
    }
    const processed = sites.map((siteRaw, index) => {
      const addedProps = {}

      if (siteRaw.time && siteRaw.timeRemoved) {
        addedProps.duration = siteRaw.timeRemoved - siteRaw.time
      }
      const solutionsRaw = siteRaw.solutions || {}
      const sortedSolutions = []
      for (const key in solutionsRaw) {
        sortedSolutions.push({
          id: key,
          ...replaceObjectRawKeys(solutionsRaw[key]),
        })
      }
      sortedSolutions.sort(specialTimeSort)

      let count = 1
      const solutions = sortedSolutions.map(sol => ({
        ...sol,
        bagNumber: sol.name !== 'None' ? count++ : null,
      }))

      /** @type {Omit<IvSite, 'placementComplete'>} */
      const site = {
        ...siteRaw,
        ...addedProps,
        active: !!(siteRaw.time && !siteRaw.timeRemoved),
        siteNumber: index + 1,
        solutions,
      }
      return site
    })

    /** @type {IvSite[]} */
    const toReturn = []
    for (const site of processed) {
      const placementComplete = placementIsComplete(site)
      if (!placementComplete) {
        toReturn.push({ ...site, placementComplete, editing: true })
      } else {
        toReturn.push({ ...site, placementComplete })
      }
    }
    return toReturn
  }
)

/** @type {CaseSelector<number | null>} */
export const selectLatestIvRemovedTime = createSelector(
  selectIvSites,
  sites => {
    let latest = 0
    for (const site of sites) {
      if (site.timeRemoved && site.timeRemoved > latest) {
        latest = site.timeRemoved
      }
    }
    return latest || null
  }
)

/** @type {CaseSelector<number | null>} */
export const selectEarliestIvPlacedTime = createSelector(
  selectIvSites,
  sites => {
    let earliest = Infinity
    for (const site of sites) {
      if (site.time && site.time < earliest) {
        earliest = site.time
      }
    }
    return earliest === Infinity ? null : earliest
  }
)

/** @type {CaseSelector<IvSite[] | null>} */
export const selectActiveIvSites = createSelector(selectIvSites, sites =>
  nullIfEmpty(sites.filter(site => site.active))
)

/**
 * Uses certain case values as an indicator of whether or not to show the
 * various detailed fluid fields and info.
 *
 * @type {CaseSelector<boolean>}
 */
export const selectUsingDetailedFluids = createSelector(
  selectIVEnabled,
  selectHasFluidDoseDetail,
  selectIvSites,
  /**
   * @param {IvSite[]} sites
   * @returns {boolean}
   */
  (ivEnabled, hasDetail, sites) =>
    ivEnabled &&
    (hasDetail ||
      sites.length > 1 ||
      sites.some(
        site =>
          site.solutions.length > 1 ||
          site.solutions.some(sol => sol.amount_raw)
      ))
)

/** @deprecated Remove this once no longer used by ac-patterns */
export const selectFirstIvSolution = createSelector(
  selectIvSites,
  locations => {
    const first =
      locations[0] && locations[0].solutions && locations[0].solutions[0]
    if (!first) {
      return null
    }
    const { name, amount } = first
    return removeKeysWithNullishValues({ solutionType: name, amount })
  }
)

export const selectIvAccessData = createSelector(selectIvSites, locations => {
  const { name, side, securedWith, attempts, needleGauge, solutions } =
    locations[0] || {}
  const firstSolution = solutions && solutions[0]
  const firstSolutionType = firstSolution && firstSolution.name
  return {
    location: name,
    side,
    securedWith,
    attempts: attempts || 0,
    needleGauge,
    solutionType: firstSolutionType || undefined,
  }
})

export const selectHasAnyIvAccessValues = createSelector(
  selectIvAccessData,
  data => {
    for (const key in data) {
      // anything truthy we'll consider true
      if (data[key]) {
        return true
      }
    }
    return false
  }
)

const getDescription = (site, isRemoval, hasMultipleSites) =>
  [
    'IV',
    hasMultipleSites && `#${site.siteNumber}`,
    isRemoval ? 'removed from' : 'placed at',
    'the',
    `${site.side} ${site.name}`,
  ]
    .filter(x => x)
    .join(' ')
/** @type {CaseSelector<FluidEvent[]>} */
export const selectFluidEvents = createSelector(
  selectIvSites,
  /** @param {IvSite[]} ivSites */ ivSites => {
    const filtered = ivSites.filter(site => site.time && site.name && site.side)
    /** @type {FluidEvent[]} */
    const toReturn = []
    const hasMultipleSites = filtered.length > 1
    for (const site of filtered) {
      toReturn.push({
        id: site.id + '_placed',
        note: getDescription(site, false, hasMultipleSites),
        type: 'ivPlaced',
        time: site.time,
        derived: true,
        showInEventReport: true,
      })
      if (site.timeRemoved) {
        toReturn.push({
          id: site.id + '_removed',
          note: getDescription(site, true, hasMultipleSites),
          type: 'ivRemoved',
          time: site.timeRemoved,
          derived: true,
          showInEventReport: true,
        })
      }
      if (site.solutions.length > 1) {
        // only do these for newIvBag events if this was not the first bag
        for (const solution of site.solutions.slice(1)) {
          if (solution.time && solution.name !== 'None') {
            toReturn.push({
              // @ts-ignore
              id: `${site.id}_${solution.id}`,
              note: `Added ${solution.name} IV bag for site #${site.siteNumber} `,
              type: 'newIvBag',
              time: solution.time,
              derived: true,
              showInEventReport: true,
            })
          }
        }
      }
    }
    return toReturn
  }
)

/** @type {CaseSelector<Record<string, number>>} */
export const selectFluidTimesByEventId = createSelector(
  selectFluidEvents,
  events => {
    const toReturn = {}
    for (const event of events) {
      toReturn[event.id] = event.time
    }
    return toReturn
  }
)
